import { apiCall } from './apiClient';
import type { ContractRecord } from './complexServicesFunctions';
import { USER_FACING_ERROR_MESSAGES } from '../utils/userFacingError';

export type SuspenseType = 'option_exercise';
export type SuspenseStatus = 'overdue' | 'at_risk' | 'upcoming' | 'complete';
export type SuspenseUrgency = 'red' | 'orange' | 'yellow' | 'green';

export interface Suspense {
  eventKey: string;
  optionPeriodId: string;
  contractNumber: string;
  contractName: string;
  title: string;
  type: SuspenseType;
  owner: string;
  dueDate: string;
  daysRemaining: number;
  status: SuspenseStatus;
  urgency: SuspenseUrgency;
}

export interface OptionPeriodView {
  optionPeriodId: string;
  label: string;
  start: string;
  end: string;
  status: string;
  reminders: Suspense[];
}

/** An authoritative activity event returned only for contracts visible to the caller. */
export interface ActivityEvent {
  eventId: string;
  activityType: string;
  status: string;
  occurredAt: string | null;
  performedBy: string | null;
  recordedBy: string | null;
  performedByRole: string | null;
  workflowStage: string | null;
  artifactLink: string | null;
  note: string | null;
  updatedAt: string | null;
  updatedBy: string | null;
}

/** Fields a caller may record manually. The server alone assigns recorder attribution. */
export interface ManualActivityEventInput {
  activityType: string;
  status: string;
  performedBy: string;
  occurredAt: string;
  note?: string | null;
}

/** Content fields that may be revised on a manually entered activity. */
export interface ManualActivityEventUpdateInput {
  activityType: string;
  status: string;
  performedBy: string;
  occurredAt: string;
  note?: string | null;
}

export interface TrackedContract {
  read_only?: boolean;
  read_only_reason?: 'super_admin_view' | 'training' | 'similar_requirements' | null;
  can_edit?: boolean;
  capabilities?: { can_edit?: boolean };
  flowId: string;
  record: ContractRecord;
  ownerEmail: string;
  ownerUnit?: string | null;
  optionPeriods: OptionPeriodView[];
  suspenses: Suspense[];
  activityEvents: ActivityEvent[];
}

/** Durable Stage-615 plan item. Planned and actual fields intentionally remain distinct. */
export interface PreAwardMilestone {
  id: string;
  label: string;
  workflowOrder: number;
  status: 'not_started' | 'in_progress' | 'blocked' | 'complete' | string;
  assignee: string | null;
  plannedStartDate: string | null;
  plannedEndDate: string | null;
  targetDate: string | null;
  actualOccurredAt: string | null;
  actualSourceActionId: string | null;
  recordedBy: string | null;
  recordedAt: string | null;
  updatedBy?: string | null;
  updatedAt?: string | null;
  provenance: string | null;
}

/** Flow-scoped schedule projection. It is intentionally separate from the tracker-list payload. */
export interface ContractSchedule {
  read_only?: boolean;
  can_edit?: boolean;
  capabilities?: { can_edit?: boolean };
  flowId: string;
  display: {
    workflowName: string | null;
    contractNumber: string | null;
    contractName: string | null;
  };
  preAwardMilestones: PreAwardMilestone[];
  optionPeriods: OptionPeriodView[];
  suspenses: Suspense[];
  activityEvents: ActivityEvent[];
}

export interface TrackerPayload {
  contracts: TrackedContract[];
  suspenses: Suspense[];
}

type WireObject = Record<string, any>;

const value = (wire: WireObject, camel: string, snake: string) => wire[camel] ?? wire[snake];

function normalizeSuspense(wire: WireObject): Suspense {
  return {
    eventKey: String(value(wire, 'eventKey', 'event_key') ?? ''),
    optionPeriodId: String(value(wire, 'optionPeriodId', 'option_period_id') ?? ''),
    contractNumber: String(value(wire, 'contractNumber', 'contract_number') ?? ''),
    contractName: String(value(wire, 'contractName', 'contract_name') ?? ''),
    title: String(wire.title ?? ''),
    type: wire.type as SuspenseType,
    owner: String(wire.owner ?? ''),
    dueDate: String(value(wire, 'dueDate', 'due_date') ?? ''),
    daysRemaining: Number(value(wire, 'daysRemaining', 'days_remaining') ?? 0),
    status: wire.status as SuspenseStatus,
    urgency: wire.urgency as SuspenseUrgency,
  };
}

function normalizeOptionPeriod(wire: WireObject): OptionPeriodView {
  return {
    optionPeriodId: String(value(wire, 'optionPeriodId', 'option_period_id') ?? ''),
    label: String(wire.label ?? ''),
    start: String(value(wire, 'start', 'start_date') ?? ''),
    end: String(value(wire, 'end', 'end_date') ?? ''),
    status: String(value(wire, 'status', 'exercise_status') ?? ''),
    reminders: (wire.reminders ?? wire.suspenses ?? []).map(normalizeSuspense),
  };
}

const optionalText = (value: unknown): string | null => (
  value === null || value === undefined || value === '' ? null : String(value)
);

function normalizeActivityEvent(wire: WireObject): ActivityEvent {
  return {
    eventId: String(value(wire, 'eventId', 'event_id') ?? ''),
    activityType: String(value(wire, 'activityType', 'activity_type') ?? ''),
    status: String(wire.status ?? wire.outcome ?? ''),
    occurredAt: optionalText(value(wire, 'occurredAt', 'occurred_at')),
    performedBy: optionalText(value(wire, 'performedBy', 'performed_by')),
    recordedBy: optionalText(value(wire, 'recordedBy', 'recorded_by')),
    performedByRole: optionalText(value(wire, 'performedByRole', 'performed_by_role') ?? value(wire, 'actorRole', 'actor_role')),
    workflowStage: optionalText(value(wire, 'workflowStage', 'workflow_stage')),
    artifactLink: optionalText(value(wire, 'artifactLink', 'artifact_link') ?? value(wire, 'artifactRef', 'artifact_ref')),
    note: optionalText(wire.note),
    updatedAt: optionalText(value(wire, 'updatedAt', 'updated_at')),
    updatedBy: optionalText(value(wire, 'updatedBy', 'updated_by')),
  };
}

function normalizePreAwardMilestone(wire: WireObject): PreAwardMilestone {
  return {
    id: String(wire.id ?? ''),
    label: String(wire.label ?? ''),
    workflowOrder: Number(value(wire, 'workflowOrder', 'workflow_order') ?? 0),
    status: String(wire.status ?? ''),
    assignee: optionalText(wire.assignee),
    plannedStartDate: optionalText(value(wire, 'plannedStartDate', 'planned_start_date')),
    plannedEndDate: optionalText(value(wire, 'plannedEndDate', 'planned_end_date')),
    targetDate: optionalText(value(wire, 'targetDate', 'target_date')),
    actualOccurredAt: optionalText(value(wire, 'actualOccurredAt', 'actual_occurred_at')),
    actualSourceActionId: optionalText(value(wire, 'actualSourceActionId', 'actual_source_action_id')),
    recordedBy: optionalText(value(wire, 'recordedBy', 'recorded_by')),
    recordedAt: optionalText(value(wire, 'recordedAt', 'recorded_at')),
    updatedBy: optionalText(value(wire, 'updatedBy', 'updated_by')),
    updatedAt: optionalText(value(wire, 'updatedAt', 'updated_at')),
    provenance: optionalText(wire.provenance),
  };
}

function normalizeWorkflowAccess(wire: WireObject): Pick<TrackedContract, 'read_only' | 'can_edit' | 'read_only_reason' | 'capabilities'> {
  const reason = wire.read_only_reason;
  return {
    ...(typeof wire.read_only === 'boolean' ? { read_only: wire.read_only } : {}),
    ...(typeof wire.can_edit === 'boolean' ? { can_edit: wire.can_edit } : {}),
    ...(reason === 'super_admin_view' || reason === 'similar_requirements' || reason === 'training'
      ? { read_only_reason: reason } : reason === null ? { read_only_reason: null } : {}),
    ...(typeof wire.capabilities?.can_edit === 'boolean'
      ? { capabilities: { can_edit: wire.capabilities.can_edit } } : {}),
  };
}

function normalizeContract(wire: WireObject): TrackedContract {
  return {
    ...normalizeWorkflowAccess(wire),
    flowId: String(value(wire, 'flowId', 'flow_id') ?? ''),
    record: wire.record as ContractRecord,
    ownerEmail: String(value(wire, 'ownerEmail', 'owner_email') ?? ''),
    ownerUnit: value(wire, 'ownerUnit', 'owner_unit') ?? null,
    optionPeriods: (value(wire, 'optionPeriods', 'option_periods') ?? []).map(normalizeOptionPeriod),
    suspenses: (wire.suspenses ?? []).map(normalizeSuspense),
    activityEvents: (value(wire, 'activityEvents', 'activity_events') ?? []).map(normalizeActivityEvent),
  };
}

/** Load the caller-visible post-award tracker projection. */
export async function getTrackedContracts(): Promise<TrackerPayload> {
  const response = await apiCall<WireObject>('/api/complex-services/tracker/contracts', 'GET');
  if (!response.success || !response.data) {
    const error = new Error(
      response.error ?? USER_FACING_ERROR_MESSAGES.maintainers_alerted,
    ) as Error & { error_code?: string; technical_detail?: string };
    error.error_code = response.error_code;
    error.technical_detail = response.technical_detail;
    throw error;
  }

  return {
    contracts: (response.data.contracts ?? []).map(normalizeContract),
    suspenses: (response.data.suspenses ?? []).map(normalizeSuspense),
  };
}

/** Load one authorized workflow's explicit pre- and post-award schedule facts. */
export async function getContractSchedule(flowId: string): Promise<ContractSchedule> {
  const response = await apiCall<WireObject>(
    `/api/complex-services/tracker/contracts/${encodeURIComponent(flowId)}/schedule`,
    'GET',
  );
  if (!response.success || !response.data) {
    const error = new Error(response.error ?? USER_FACING_ERROR_MESSAGES.maintainers_alerted) as Error & {
      error_code?: string; technical_detail?: string;
    };
    error.error_code = response.error_code;
    error.technical_detail = response.technical_detail;
    throw error;
  }
  const wire = response.data;
  const display = (wire.display ?? {}) as WireObject;
  return {
    ...normalizeWorkflowAccess(wire),
    flowId: String(value(wire, 'flowId', 'flow_id') ?? flowId),
    display: {
      workflowName: optionalText(value(display, 'workflowName', 'workflow_name')),
      contractNumber: optionalText(value(display, 'contractNumber', 'contract_number')),
      contractName: optionalText(value(display, 'contractName', 'contract_name')),
    },
    preAwardMilestones: (value(wire, 'preAwardMilestones', 'pre_award_milestones') ?? []).map(normalizePreAwardMilestone),
    optionPeriods: (value(wire, 'optionPeriods', 'option_periods') ?? []).map(normalizeOptionPeriod),
    suspenses: (wire.suspenses ?? []).map(normalizeSuspense),
    activityEvents: (value(wire, 'activityEvents', 'activity_events') ?? []).map(normalizeActivityEvent),
  };
}

/** Record a manual activity entry. The server assigns recorder attribution. */
export async function createManualActivityEvent(
  flowId: string,
  input: ManualActivityEventInput,
): Promise<ActivityEvent> {
  const response = await apiCall<WireObject>(
    `/api/complex-services/tracker/contracts/${encodeURIComponent(flowId)}/activity`,
    'POST',
    {
      activity_type: input.activityType,
      outcome: input.status,
      performed_by: input.performedBy,
      occurred_at: input.occurredAt,
      note: input.note || null,
    },
  );
  if (!response.success || !response.data) {
    const error = new Error(
      response.error ?? USER_FACING_ERROR_MESSAGES.maintainers_alerted,
    ) as Error & { error_code?: string; technical_detail?: string };
    error.error_code = response.error_code;
    error.technical_detail = response.technical_detail;
    throw error;
  }
  return normalizeActivityEvent(response.data.event ?? response.data);
}

/** Revise permitted content on a manually entered event; provenance stays server-owned. */
export async function updateManualActivityEvent(
  flowId: string,
  eventId: string,
  input: ManualActivityEventUpdateInput,
): Promise<ActivityEvent> {
  const response = await apiCall<WireObject>(
    `/api/complex-services/tracker/contracts/${encodeURIComponent(flowId)}/activity/${encodeURIComponent(eventId)}`,
    'PATCH',
    {
      activity_type: input.activityType,
      outcome: input.status,
      performed_by: input.performedBy,
      occurred_at: input.occurredAt,
      note: input.note || null,
    },
  );
  if (!response.success || !response.data) {
    const error = new Error(
      response.error ?? USER_FACING_ERROR_MESSAGES.maintainers_alerted,
    ) as Error & { error_code?: string; technical_detail?: string };
    error.error_code = response.error_code;
    error.technical_detail = response.technical_detail;
    throw error;
  }
  return normalizeActivityEvent(response.data.event ?? response.data);
}
